import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock, Unlock, Send, CheckCircle, Clock, AlertTriangle,
  Banknote, Building2, ArrowRight, Shield, Info, Loader2, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getDateLocale } from '../../utils/dateLocale';

const STATUS_CONFIG = {
  active: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Lock, desc: 'Inscriptions ouvertes, fonds en sequestre' },
  registrations_closed: { label: 'Inscriptions cloturees', color: 'bg-amber-100 text-amber-700', icon: Clock, desc: 'Pret pour demande de virement' },
  transfer_requested: { label: 'Virement demande', color: 'bg-orange-100 text-orange-700', icon: Send, desc: 'En attente du virement par iBookSport' },
  transfer_completed: { label: 'Virement effectue', color: 'bg-green-100 text-green-700', icon: CheckCircle, desc: 'Fonds transferes sur votre compte' }
};

export const EscrowSection = () => {
  const [escrows, setEscrows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedEscrow, setSelectedEscrow] = useState(null);
  const [iban, setIban] = useState('');
  const [ibanHolder, setIbanHolder] = useState('');
  const [ibanConfirmed, setIbanConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(null);

  const fetchEscrows = async () => {
    try {
      const res = await api.get('/organizer/escrow');
      setEscrows(res.data.escrows || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      console.error('Escrow fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEscrows(); }, []);

  const handleCloseRegistrations = async (eventId) => {
    try {
      await api.post(`/organizer/escrow/${eventId}/close-registrations`);
      toast.success('Inscriptions cloturees');
      setShowCloseConfirm(null);
      fetchEscrows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleRequestTransfer = async () => {
    if (!iban || iban.replace(/\s/g, '').length < 15) { toast.error('IBAN invalide'); return; }
    if (!ibanHolder.trim()) { toast.error('Nom du titulaire requis'); return; }
    if (!ibanConfirmed) { toast.error('Vous devez confirmer que l\'IBAN est correct'); return; }
    setSubmitting(true);
    try {
      await api.post(`/organizer/escrow/${selectedEscrow.event_id}/request-transfer`, {
        iban: iban.replace(/\s/g, ''),
        iban_holder_name: ibanHolder.trim()
      });
      toast.success('Demande de virement envoyee !');
      setShowTransferModal(false);
      setIban(''); setIbanHolder(''); setIbanConfirmed(false); setSelectedEscrow(null);
      fetchEscrows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-brand" /></div>;

  return (
    <div className="space-y-6" data-testid="escrow-section">
      {/* Summary Banner */}
      {summary && (
        <div className="bg-asphalt text-white p-6 border-l-4 border-brand">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-brand" />
            <h3 className="font-heading font-bold uppercase text-sm">Comptes Sequestres</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] font-heading uppercase text-slate-400">En sequestre</p>
              <p className="text-2xl font-heading font-extrabold text-blue-400" data-testid="escrow-total-held">{summary.total_in_escrow.toLocaleString()}€</p>
            </div>
            <div>
              <p className="text-[10px] font-heading uppercase text-slate-400">Virement demande</p>
              <p className="text-2xl font-heading font-extrabold text-amber-400">{summary.pending_transfer.toLocaleString()}€</p>
            </div>
            <div>
              <p className="text-[10px] font-heading uppercase text-slate-400">Deja vire</p>
              <p className="text-2xl font-heading font-extrabold text-green-400" data-testid="escrow-total-transferred">{summary.total_transferred.toLocaleString()}€</p>
            </div>
            <div>
              <p className="text-[10px] font-heading uppercase text-slate-400">Evenements</p>
              <p className="text-2xl font-heading font-extrabold text-white">{summary.count}</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-bold mb-1">Comment fonctionne le compte sequestre ?</p>
          <p className="text-xs text-blue-600 leading-relaxed">
            Pour chaque evenement payant, les inscriptions sont collectees sur un compte sequestre iBookSport (hors frais de fonctionnement de 5%).
            A la fin des inscriptions, cloturez les inscriptions puis demandez le virement en fournissant votre IBAN.
            L'administrateur effectuera le virement sous 5 jours ouvrables.
          </p>
        </div>
      </div>

      {/* Escrow Cards */}
      {escrows.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400">
          <Lock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-heading font-bold">Aucun compte sequestre</p>
          <p className="text-sm mt-1">Les comptes sequestres apparaitront automatiquement lors de la premiere inscription payante a vos evenements.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {escrows.map((esc) => {
            const cfg = STATUS_CONFIG[esc.status] || STATUS_CONFIG.active;
            const Icon = cfg.icon;
            return (
              <motion.div key={esc.escrow_id || esc.event_id} className="bg-white border border-slate-200 overflow-hidden" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid={`escrow-card-${esc.event_id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading font-bold text-lg truncate">{esc.event_title}</h4>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        {esc.event_date && <span>{format(new Date(esc.event_date), 'd MMM yyyy', { locale: getDateLocale() })}</span>}
                        <span>{esc.current_participants || 0} / {esc.max_participants || 0} inscrits</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-bold uppercase flex items-center gap-1.5 ${cfg.color}`}>
                      <Icon className="w-3.5 h-3.5" /> {cfg.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100">
                    <div>
                      <p className="text-[10px] font-heading uppercase text-slate-400">Ventes inscriptions</p>
                      <p className="text-xl font-heading font-extrabold">{esc.registrations_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-heading uppercase text-slate-400">Montant sequestre</p>
                      <p className="text-xl font-heading font-extrabold text-blue-600" data-testid={`escrow-amount-${esc.event_id}`}>{(esc.total_sales || 0).toLocaleString()}€</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-heading uppercase text-slate-400">Frais plateforme (5%)</p>
                      <p className="text-xl font-heading font-extrabold text-red-500">-{(esc.platform_fees || 0).toLocaleString()}€</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-heading uppercase text-slate-400">Montant net a recevoir</p>
                      <p className="text-xl font-heading font-extrabold text-green-600">{(esc.total_sales || 0).toLocaleString()}€</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3 flex-wrap">
                    {esc.status === 'active' && (
                      <>
                        {showCloseConfirm === esc.event_id ? (
                          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 p-3 w-full">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                            <p className="text-xs text-amber-800 flex-1">Cette action est irreversible. Plus aucune inscription ne sera acceptee.</p>
                            <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700 text-xs" onClick={() => handleCloseRegistrations(esc.event_id)} data-testid={`escrow-confirm-close-${esc.event_id}`}>Confirmer la cloture</Button>
                            <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowCloseConfirm(null)}>Annuler</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="gap-1.5 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setShowCloseConfirm(esc.event_id)} data-testid={`escrow-close-btn-${esc.event_id}`}>
                            <Lock className="w-3.5 h-3.5" /> Cloturer les inscriptions
                          </Button>
                        )}
                      </>
                    )}

                    {esc.status === 'registrations_closed' && (
                      <Button size="sm" className="bg-brand text-white hover:bg-brand/90 gap-1.5 text-xs" onClick={() => { setSelectedEscrow(esc); setShowTransferModal(true); }} data-testid={`escrow-request-transfer-${esc.event_id}`}>
                        <Banknote className="w-3.5 h-3.5" /> Demander le virement
                      </Button>
                    )}

                    {esc.status === 'transfer_requested' && (
                      <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-50 px-3 py-2">
                        <Clock className="w-4 h-4" />
                        <span>Virement en attente - IBAN: {esc.iban?.substring(0, 4)}****{esc.iban?.slice(-4)} - Demande le {esc.transfer_requested_at ? format(new Date(esc.transfer_requested_at), 'd MMM yyyy', { locale: getDateLocale() }) : ''}</span>
                      </div>
                    )}

                    {esc.status === 'transfer_completed' && (
                      <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-3 py-2">
                        <CheckCircle className="w-4 h-4" />
                        <span>Virement effectue le {esc.transfer_completed_at ? format(new Date(esc.transfer_completed_at), 'd MMM yyyy', { locale: getDateLocale() }) : ''} - {(esc.total_sales || 0).toLocaleString()}€</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Transfer Request Modal */}
      <Dialog open={showTransferModal} onOpenChange={(open) => { if (!open) { setShowTransferModal(false); setIban(''); setIbanHolder(''); setIbanConfirmed(false); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Demande de virement</DialogTitle>
            <DialogDescription>
              {selectedEscrow?.event_title} - Montant : <strong className="text-green-600">{(selectedEscrow?.total_sales || 0).toLocaleString()}€</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="iban_holder" className="text-sm font-bold">Titulaire du compte bancaire</Label>
              <Input id="iban_holder" value={ibanHolder} onChange={(e) => setIbanHolder(e.target.value)} placeholder="Nom et prenom ou raison sociale" className="mt-1" data-testid="escrow-iban-holder" />
            </div>

            <div>
              <Label htmlFor="iban" className="text-sm font-bold">IBAN</Label>
              <Input id="iban" value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())} placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" className="mt-1 font-mono tracking-wide" data-testid="escrow-iban-input" />
            </div>

            <div className="bg-red-50 border border-red-200 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-red-800">ATTENTION - IBAN DEFINITIF</p>
                  <p className="text-xs text-red-600 mt-1 leading-relaxed">
                    L'IBAN que vous saisissez est <strong>definitif et ne pourra pas etre modifie</strong> apres validation.
                    Toute erreur de saisie sera a la charge exclusive de l'organisateur.
                    <strong> iBookSport ne pourra etre tenu responsable</strong> d'une erreur d'IBAN commise par l'organisateur.
                  </p>
                </div>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={ibanConfirmed} onChange={(e) => setIbanConfirmed(e.target.checked)} className="mt-1 w-4 h-4 accent-brand" data-testid="escrow-iban-confirm-checkbox" />
              <span className="text-xs text-slate-600 leading-relaxed">
                Je confirme que l'IBAN saisi est correct et j'accepte que toute erreur de saisie soit a ma charge exclusive. Je comprends que cet IBAN ne pourra pas etre modifie et qu'iBookSport ne pourra etre tenu responsable en cas d'erreur.
              </span>
            </label>

            <div className="flex gap-3 pt-2">
              <Button className="flex-1 bg-brand text-white hover:bg-brand/90 gap-2" onClick={handleRequestTransfer} disabled={submitting || !ibanConfirmed} data-testid="escrow-submit-transfer">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer la demande de virement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
